import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChatbubbleEllipsesOutline, IoChevronBack, IoAdd, IoCloseOutline, IoPersonCircleOutline } from 'react-icons/io5';
import { MdPerson } from 'react-icons/md';
import { db } from '../../firebase/config';
import {
  collection, query, where, onSnapshot, orderBy, getDocs, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { format, isToday, isYesterday } from 'date-fns';

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isToday(date)) return format(date, 'a h:mm').replace('AM', '오전').replace('PM', '오후');
  if (isYesterday(date)) return '어제';
  return format(date, 'M/d');
};

const ROLE_LABELS = { teacher: '선생님', student: '학생', parent: '학부모', assistant: '조교' };

const ChatPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const myUid = userData?.uid;
  const myRole = userData?.role;

  // 채팅방 목록 실시간 조회
  useEffect(() => {
    if (!myUid) return;
    const q = query(
      collection(db, 'chatRooms'),
      where('participants', 'array-contains', myUid),
      orderBy('lastMessageAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setRooms(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [myUid]);

  const getRoomDisplayName = (room) => {
    if (room.roomName) return room.roomName;
    const names = room.participantNames || {};
    const otherUid = (room.participants || []).find(uid => uid !== myUid);
    return names[otherUid] || '알 수 없음';
  };

  const getRoomRole = (room) => {
    const otherUid = (room.participants || []).find(uid => uid !== myUid);
    const roles = room.participantRoles || {};
    return roles[otherUid] || '';
  };

  const getUnread = (room) => (room.unreadCount || {})[myUid] || 0;

  // 새 채팅 — 같은 클래스 소속 + 역할 필터링된 사용자 목록
  const openNewChat = async () => {
    setShowNewChat(true);
    setUserSearch('');
    setLoadingUsers(true);
    try {
      // 1. 내가 속한 클래스 조회
      const classSnap = await getDocs(collection(db, 'classes'));
      const myClasses = classSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => {
          const ids = c.studentIds || [];
          const teacherId = c.teacherId || '';
          const assistantIds = c.assistantIds || [];
          return ids.includes(myUid) || teacherId === myUid || assistantIds.includes(myUid);
        });

      // 2. 클래스에 속한 모든 사용자 UID 수집
      const relatedUids = new Set();
      myClasses.forEach(c => {
        if (c.teacherId) relatedUids.add(c.teacherId);
        (c.studentIds || []).forEach(uid => relatedUids.add(uid));
        (c.assistantIds || []).forEach(uid => relatedUids.add(uid));
      });
      relatedUids.delete(myUid);

      // 3. 사용자 정보 조회
      if (relatedUids.size === 0) {
        setChatUsers([]);
        setLoadingUsers(false);
        return;
      }

      const userSnap = await getDocs(collection(db, 'users'));
      const allUsers = userSnap.docs.map(d => ({ uid: d.id, ...d.data() }));

      // 4. 역할 기반 필터 (선생님↔학생, 선생님↔학부모)
      const filtered = allUsers.filter(u => {
        if (!relatedUids.has(u.uid)) return false;
        if (myRole === 'teacher' || myRole === 'assistant') {
          return ['student', 'parent'].includes(u.role);
        }
        if (myRole === 'student' || myRole === 'parent') {
          return ['teacher', 'assistant'].includes(u.role);
        }
        return false;
      });

      // 5. 학부모의 경우 — 자녀의 클래스 선생님만
      // (이미 relatedUids로 필터됨)

      setChatUsers(filtered);
    } catch (err) {
      console.error('사용자 목록 로드 실패:', err);
      setChatUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // 채팅 시작 (기존 방 있으면 재사용)
  const startChat = async (partner) => {
    try {
      // 기존 1:1 채팅방 찾기
      const q = query(
        collection(db, 'chatRooms'),
        where('participants', 'array-contains', myUid)
      );
      const snap = await getDocs(q);
      const existing = snap.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(partner.uid) && data.participants.length === 2;
      });

      if (existing) {
        setShowNewChat(false);
        navigate(`/chat/${existing.id}`);
        return;
      }

      // 새 채팅방 생성
      const newRoom = await addDoc(collection(db, 'chatRooms'), {
        participants: [myUid, partner.uid],
        participantNames: {
          [myUid]: userData?.name || '',
          [partner.uid]: partner.name || '',
        },
        participantRoles: {
          [myUid]: myRole || '',
          [partner.uid]: partner.role || '',
        },
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadCount: { [myUid]: 0, [partner.uid]: 0 },
      });

      setShowNewChat(false);
      navigate(`/chat/${newRoom.id}`);
    } catch (err) {
      console.error('채팅 시작 실패:', err);
    }
  };

  const filteredChatUsers = chatUsers.filter(u => {
    if (!userSearch) return true;
    return (u.name || '').toLowerCase().includes(userSearch.toLowerCase());
  });

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate('/home')}><IoChevronBack size={24} /></BackBtn>
        <HeaderTitle>채팅</HeaderTitle>
        <NewChatBtn onClick={openNewChat}><IoAdd size={24} /></NewChatBtn>
      </Header>

      <RoomList>
        {loading ? (
          <EmptyState><EmptyText>로딩 중...</EmptyText></EmptyState>
        ) : rooms.length === 0 ? (
          <EmptyState>
            <IoChatbubbleEllipsesOutline size={40} color="#D1D5DB" />
            <EmptyText>채팅방이 없습니다</EmptyText>
            <EmptySubText>+ 버튼을 눌러 대화를 시작하세요</EmptySubText>
          </EmptyState>
        ) : (
          rooms.map(room => {
            const unread = getUnread(room);
            const roleLabel = ROLE_LABELS[getRoomRole(room)] || '';
            return (
              <RoomItem key={room.id} onClick={() => navigate(`/chat/${room.id}`)}>
                <Avatar>
                  <MdPerson size={28} color="#9CA3AF" />
                </Avatar>
                <RoomInfo>
                  <RoomNameRow>
                    <RoomName>{getRoomDisplayName(room)}</RoomName>
                    {roleLabel && <RoleBadge>{roleLabel}</RoleBadge>}
                  </RoomNameRow>
                  <LastMessage>{room.lastMessage || '대화를 시작해보세요'}</LastMessage>
                </RoomInfo>
                <RoomMeta>
                  <RoomTime>{formatTime(room.lastMessageAt)}</RoomTime>
                  {unread > 0 && <UnreadBadge>{unread > 99 ? '99+' : unread}</UnreadBadge>}
                </RoomMeta>
              </RoomItem>
            );
          })
        )}
      </RoomList>

      {/* 새 채팅 모달 */}
      {showNewChat && (
        <ModalOverlay onClick={() => setShowNewChat(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>새 채팅</ModalTitle>
              <ModalCloseBtn onClick={() => setShowNewChat(false)}>
                <IoCloseOutline size={22} />
              </ModalCloseBtn>
            </ModalHeader>
            <SearchInput
              placeholder="이름 검색..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
            />
            <UserList>
              {loadingUsers ? (
                <UserEmptyText>사용자 목록을 불러오는 중...</UserEmptyText>
              ) : filteredChatUsers.length === 0 ? (
                <UserEmptyText>대화 가능한 사용자가 없습니다</UserEmptyText>
              ) : (
                filteredChatUsers.map(u => (
                  <UserItem key={u.uid} onClick={() => startChat(u)}>
                    <UserAvatar><MdPerson size={22} color="white" /></UserAvatar>
                    <UserInfo>
                      <UserName>{u.name}</UserName>
                      <UserRole>{ROLE_LABELS[u.role] || u.role}</UserRole>
                    </UserInfo>
                  </UserItem>
                ))
              )}
            </UserList>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ChatPage;

/* ─── Styled Components ──────────────────── */

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  min-height: 56px;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  color: #1A1A2E;
  padding: 4px;
`;

const HeaderTitle = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #1A1A2E;
`;

const NewChatBtn = styled.button`
  display: flex;
  align-items: center;
  color: #4A7CFE;
  padding: 4px;
`;

const RoomList = styled.div`
  padding: 0 16px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 80px 0;
`;

const EmptyText = styled.p`
  font-size: 15px;
  color: #9CA3AF;
  font-weight: 500;
`;

const EmptySubText = styled.p`
  font-size: 13px;
  color: #D1D5DB;
`;

const RoomItem = styled.div`
  display: flex;
  gap: 14px;
  padding: 16px 4px;
  border-bottom: 1px solid #F3F4F6;
  cursor: pointer;
  align-items: center;
  &:active { background: #F7F8FA; }
  &:last-child { border-bottom: none; }
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #F3F4F6;
`;

const RoomInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RoomNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RoomName = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #1A1A2E;
`;

const RoleBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #6B7280;
  background: #F3F4F6;
  padding: 2px 6px;
  border-radius: 4px;
`;

const LastMessage = styled.p`
  font-size: 13px;
  color: #9CA3AF;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RoomMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  align-self: flex-start;
  padding-top: 2px;
`;

const RoomTime = styled.span`
  font-size: 12px;
  color: #9CA3AF;
  white-space: nowrap;
`;

const UnreadBadge = styled.span`
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: #4A7CFE;
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
`;

/* ─── 새 채팅 모달 ──────────────────── */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 360px;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
`;

const ModalTitle = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: #1A1A2E;
`;

const ModalCloseBtn = styled.button`
  color: #9CA3AF;
  display: flex;
  align-items: center;
  padding: 2px;
`;

const SearchInput = styled.input`
  margin: 0 16px 8px;
  padding: 10px 14px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #4A7CFE; }
`;

const UserList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
`;

const UserEmptyText = styled.p`
  font-size: 14px;
  color: #9CA3AF;
  text-align: center;
  padding: 40px 0;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 4px;
  border-bottom: 1px solid #F3F4F6;
  cursor: pointer;
  &:active { background: #F7F8FA; }
  &:last-child { border-bottom: none; }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #4A7CFE;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #1A1A2E;
`;

const UserRole = styled.p`
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 2px;
`;
